import { ref, computed } from 'vue'
import { MeasurementPrefUtility } from '../utils/helpers/MeasurementPref'

// Shared hike-filter state + GraphQL variable mapping, extracted from the Hikes
// list so the Completed Hikes list can reuse the exact same filter behaviour.
// Deliberately omits the "hide completed" filter (meaningless on a completed
// list). Each filter keeps a draft value (the open dropdown) and an applied
// value (what the query actually uses); Apply copies draft -> applied.
//
// The distance/elevation sliders work in the user's chosen units (km/mi, m/ft,
// …) for display, but the GraphQL query always receives metres.
export const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Difficult', 'Extra Difficult']

// Canonical slider ceilings, in metric. Reaching the ceiling means "no max".
const DISTANCE_MAX_KM = 200
const ELEVATION_MAX_M = 5000

// Step size of the elevation slider — MUST match HikeFilterBar.vue's
// `:interval`. vue-slider-component requires (max - min) to be divisible by the
// interval; if it isn't, it emits NaN values and the knobs jam. Since the
// ceiling is converted into the user's unit (e.g. 5000 m -> ~16404 ft, not a
// multiple of 50) we round it up to a multiple of this step. Distance uses an
// interval of 1, so its (already integer) ceiling is always divisible.
const ELEVATION_SLIDER_INTERVAL = 50

export function useHikeFilters () {
  const pref = MeasurementPrefUtility.preference()

  // Slider ceilings + unit symbols in the user's units.
  const distanceMaxBound = Math.round(pref.bigUnitValue(DISTANCE_MAX_KM))
  const elevationMaxBound =
    Math.ceil(pref.smallUnitValue(ELEVATION_MAX_M) / ELEVATION_SLIDER_INTERVAL) * ELEVATION_SLIDER_INTERVAL
  const distanceUnitSymbol = pref.kilometerSymbol()
  const elevationUnitSymbol = pref.meterSymbol()

  const searchString = ref('')

  const startDateApplied = ref<string | null>(null)
  const endDateApplied   = ref<string | null>(null)
  const startDateDraft   = ref<string | null>(null)
  const endDateDraft     = ref<string | null>(null)

  // Range values are in the user's units (see distanceUnitSymbol/elevationUnitSymbol).
  const distanceRangeApplied = ref<[number, number]>([0, distanceMaxBound])
  const distanceRangeDraft   = ref<[number, number]>([0, distanceMaxBound])

  const elevationRangeApplied = ref<[number, number]>([0, elevationMaxBound])
  const elevationRangeDraft   = ref<[number, number]>([0, elevationMaxBound])

  const sortApplied = ref('created_desc')
  const sortDraft   = ref('created_desc')

  const difficultyApplied = ref<string[]>([])
  const difficultyDraft   = ref<string[]>([])

  const featureTagApplied = ref<string | null>(null)
  const featureTagDraft   = ref<string | null>(null)
  const activityTagApplied = ref<string | null>(null)
  const activityTagDraft   = ref<string | null>(null)

  // Tag option lists are populated by the host (from the hikes payload).
  const featureTags  = ref<string[]>([])
  const activityTags = ref<string[]>([])

  /* ---------- Active-state flags ---------- */
  const isDistanceFilterActive = computed(() =>
    distanceRangeApplied.value[0] !== 0 || distanceRangeApplied.value[1] !== distanceMaxBound)
  const isElevationFilterActive = computed(() =>
    elevationRangeApplied.value[0] !== 0 || elevationRangeApplied.value[1] !== elevationMaxBound)
  const isDifficultyFilterActive = computed(() => difficultyApplied.value.length > 0)
  const isFeatureTagFilterActive = computed(() => featureTagApplied.value !== null)
  const isActivityTagFilterActive = computed(() => activityTagApplied.value !== null)
  const isDateFilterActive = computed(() => startDateApplied.value !== null || endDateApplied.value !== null)
  const isSortFilterActive = computed(() => sortApplied.value !== 'created_desc')

  const hasActiveFilters = computed(() =>
    isDistanceFilterActive.value || isElevationFilterActive.value ||
    isDifficultyFilterActive.value || isFeatureTagFilterActive.value ||
    isActivityTagFilterActive.value || isDateFilterActive.value ||
    isSortFilterActive.value || searchString.value.trim() !== '')

  /* ---------- GraphQL variables ---------- */
  // Maps the applied filter state to the variables the completed-hikes / hikes
  // queries accept. Slider values are in the user's units, so convert to metres
  // here. `undefined` keys are dropped by GraphQL; range bounds at their slider
  // ceiling map to `null` (= no bound).
  function buildFilterVariables () {
    return {
      query: (searchString.value || '').trim() || null,
      distanceMetersMin: (distanceRangeApplied.value?.[0] || 0) > 0
        ? Math.round(pref.metresFromBigUnit(distanceRangeApplied.value[0])) : null,
      distanceMetersMax: distanceRangeApplied.value?.[1] >= distanceMaxBound
        ? null : Math.round(pref.metresFromBigUnit(distanceRangeApplied.value[1])),
      elevationGainMetersMin: (elevationRangeApplied.value?.[0] || 0) > 0
        ? Math.round(pref.metresFromSmallUnit(elevationRangeApplied.value[0])) : null,
      elevationGainMetersMax: elevationRangeApplied.value?.[1] >= elevationMaxBound
        ? null : Math.round(pref.metresFromSmallUnit(elevationRangeApplied.value[1])),
      difficulties: (difficultyApplied.value?.length || 0) > 0 ? difficultyApplied.value : undefined,
      featureTags: featureTagApplied.value ? [featureTagApplied.value] : undefined,
      activityTags: activityTagApplied.value ? [activityTagApplied.value] : undefined,
      startDate: startDateApplied.value || null,
      endDate: endDateApplied.value || null,
      sort: sortApplied.value
    }
  }

  /* ---------- Apply (draft -> applied) ---------- */
  const applyDistance   = () => { distanceRangeApplied.value = [...distanceRangeDraft.value] }
  const applyElevation  = () => { elevationRangeApplied.value = [...elevationRangeDraft.value] }
  const applyDifficulty = () => { difficultyApplied.value = [...difficultyDraft.value] }
  const applyFeatureTag = () => { featureTagApplied.value = featureTagDraft.value }
  const applyActivityTag = () => { activityTagApplied.value = activityTagDraft.value }
  const applyDate       = () => { startDateApplied.value = startDateDraft.value; endDateApplied.value = endDateDraft.value }
  const applySort       = () => { sortApplied.value = sortDraft.value }

  /* ---------- Reset (one filter back to default) ---------- */
  const resetDistance   = () => { distanceRangeDraft.value = [0, distanceMaxBound]; distanceRangeApplied.value = [0, distanceMaxBound] }
  const resetElevation  = () => { elevationRangeDraft.value = [0, elevationMaxBound]; elevationRangeApplied.value = [0, elevationMaxBound] }
  const resetDifficulty = () => { difficultyDraft.value = []; difficultyApplied.value = [] }
  const resetFeatureTag = () => { featureTagDraft.value = null; featureTagApplied.value = null }
  const resetActivityTag = () => { activityTagDraft.value = null; activityTagApplied.value = null }
  const resetDate       = () => { startDateDraft.value = null; endDateDraft.value = null; startDateApplied.value = null; endDateApplied.value = null }
  const resetSort       = () => { sortDraft.value = 'created_desc'; sortApplied.value = 'created_desc' }

  // Re-sync a dropdown's draft to the applied value when it opens, so cancelling
  // (closing without Apply) discards in-progress edits.
  function syncDraftsFromApplied () {
    distanceRangeDraft.value  = [...distanceRangeApplied.value]
    elevationRangeDraft.value = [...elevationRangeApplied.value]
    difficultyDraft.value     = [...difficultyApplied.value]
    featureTagDraft.value     = featureTagApplied.value
    activityTagDraft.value    = activityTagApplied.value
    startDateDraft.value      = startDateApplied.value
    endDateDraft.value        = endDateApplied.value
    sortDraft.value           = sortApplied.value
  }

  function clearAll () {
    searchString.value = ''
    resetDistance(); resetElevation(); resetDifficulty()
    resetFeatureTag(); resetActivityTag(); resetDate(); resetSort()
  }

  return {
    distanceMaxBound, elevationMaxBound, distanceUnitSymbol, elevationUnitSymbol,
    searchString,
    startDateApplied, endDateApplied, startDateDraft, endDateDraft,
    distanceRangeApplied, distanceRangeDraft,
    elevationRangeApplied, elevationRangeDraft,
    sortApplied, sortDraft,
    difficultyApplied, difficultyDraft,
    featureTagApplied, featureTagDraft, activityTagApplied, activityTagDraft,
    featureTags, activityTags,
    DIFFICULTY_OPTIONS,
    isDistanceFilterActive, isElevationFilterActive, isDifficultyFilterActive,
    isFeatureTagFilterActive, isActivityTagFilterActive, isDateFilterActive, isSortFilterActive,
    hasActiveFilters,
    buildFilterVariables,
    applyDistance, applyElevation, applyDifficulty, applyFeatureTag, applyActivityTag, applyDate, applySort,
    resetDistance, resetElevation, resetDifficulty, resetFeatureTag, resetActivityTag, resetDate, resetSort,
    syncDraftsFromApplied, clearAll
  }
}
